import React, { useState } from 'react';
import { Typography, Button, Box, Paper, Grid, Container, List, ListItem, ListItemText, Chip } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useDispatch, useSelector } from 'react-redux';
import { advanceState } from '../../gamestate/GameState';
import { selectGameObject } from '../../gamestate/gameStateSlice';
import { selectAllPlayers, selectPlayersReadinessForInstructions } from '../../player/playerSlice';
import { interruptNarration, selectCurrentNarration, selectHostStatus, HOST_STATUS } from '../hostStateSlice';
import Background from 'features/components/BackgroundEffect';


// Define brand colors
const ACCENT_TEAL = '#2ec4b6';
const ACCENT_YELLOW = '#ffe066';
const TEXT_COLOR = '#222';
const CARD_BG = '#fff';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Root = styled('div')({
  width: '100%',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: `radial-gradient(circle at top left, ${ACCENT_YELLOW}22 0%, ${ACCENT_TEAL}22 100%)`,
  padding: '24px',
  overflow: 'auto',
});

const InstructionsCard = styled(Paper)(({ theme }) => ({
  backgroundColor: CARD_BG,
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  padding: theme.spacing(2),
  maxWidth: 900,
  width: '100%',
  animation: `${fadeInUp} 0.6s ease-out`,
  overflow: 'visible',
  maxHeight: 'none',
}));

const Title = styled(Typography)({
  fontFamily: "'Gyparody', 'Bangers', 'Impact', sans-serif",
  fontSize: '2.5rem',
  fontWeight: 800,
  color: TEXT_COLOR,
  marginBottom: 10,
  textAlign: 'center',
  textShadow: '2px 2px 0px rgba(46, 196, 182, 0.3)',
});

const InstructionItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1),
  animation: `${fadeInUp} 0.4s ease-out forwards`,
  opacity: 0,
}));

const Emoji = styled(Typography)({
  fontSize: '1.6rem',
  marginRight: '12px',
  lineHeight: 1.2,
});

const InstructionText = styled(Typography)({
  fontSize: '1.4rem',
  fontWeight: 500,
  color: TEXT_COLOR,
  lineHeight: 1.3,
  flexGrow: 1,
});

// Animation keyframes
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 224, 102, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 224, 102, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 224, 102, 0);
  }
`;

const PlayerReadinessPanel = styled(Box)(({ theme }) => ({
  position: 'fixed',
  right: theme.spacing(3),
  top: theme.spacing(3),
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  borderRadius: 12,
  padding: theme.spacing(2),
  width: 250,
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  animation: `${fadeInUp} 0.5s ease-out`,
  zIndex: 10,
}));

const PlayerStatusListItem = styled(ListItem)(({ isready }) => ({
  padding: '4px 0',
  transition: 'all 0.3s ease',
  opacity: isready === 'true' ? 1 : 0.7,
}));

const SkipButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(120deg, ${ACCENT_YELLOW} 0%, ${ACCENT_TEAL} 50%, ${ACCENT_YELLOW} 100%)`,
  backgroundSize: '200% 200%',
  backgroundPosition: '0% 40%',
  color: TEXT_COLOR,
  padding: theme.spacing(1.5, 4),
  borderRadius: 12,
  fontWeight: 700,
  fontSize: '1.2em',
  textTransform: 'none',
  boxShadow: '0 4px 16px rgba(44,62,80,0.40) !important',
  minWidth: 240,
  position: 'fixed',
  bottom: theme.spacing(4),
  left: '50%',
  transform: 'translateX(-50%)',
  transition: 'background-position 0.5s cubic-bezier(0.4,0,0.2,1), box-shadow 0s',
  animation: `${pulse} 3s infinite`,
  '&:hover': {
    backgroundPosition: '100% 60%',
    boxShadow: '0 4px 16px rgba(44,62,80,0.40) !important',
  },
  '&.Mui-disabled': {
    background: '#e8e0c0',
    color: '#777570',
    boxShadow: 'none !important',
    cursor: 'not-allowed',
  }
}));

const instructionsData = [
  { emoji: '', text: "Here's how Groupardy works…" },
  { emoji: '🧠', text: "Everyone answers at the same time. Fast thinkers win." },
  { emoji: '📱', text: "Use your phone to answer before time runs out." },
  { emoji: '✅', text: "Right answers earn points. Wrong answers lose points." },
  { emoji: '⚡', text: "Fastest correct answer? +10 bonus points and control the board!" },
  { emoji: '🎯', text: "Pick your next clue from your phone." },
  { emoji: '🃏', text: "Daily Doubles - wager big, win (or lose) big." },
  { emoji: '🏁', text: "Final round has one question with all-or-nothing wager." },
  { emoji: '😎', text: "We'll do a practice round first (scores reset after)." },
  { emoji: '👉', text: "Let's go!" }
];

const InstructionsScreen = () => {
  const dispatch = useDispatch();

  const gameObj = useSelector(selectGameObject);
  const allPlayers = useSelector(selectAllPlayers);
  const playersReadiness = useSelector(selectPlayersReadinessForInstructions);
  const currentNarration = useSelector(selectCurrentNarration);
  const hostStatus = useSelector(selectHostStatus);

  const [autoAdvancing, setAutoAdvancing] = useState(false);

  // Calculate player readiness stats
  const totalPlayers = allPlayers.length;
  const readyPlayers = Object.values(playersReadiness).filter(p => p.isReady).length;
  const allPlayersReady = readyPlayers === totalPlayers && totalPlayers > 0;

  const handleAdvanceToPractice = async () => {
    if (autoAdvancing) return;
    setAutoAdvancing(true);

    if (hostStatus === HOST_STATUS.SPEAKING && currentNarration && gameObj) {
      await dispatch(interruptNarration({ gameId: gameObj.id, narrationId: currentNarration.id }));
    }

    if (gameObj) {
      await advanceState(gameObj, allPlayers, dispatch);
    }
  };

  // Determine button text based on player readiness
  const getButtonText = () => {
    if (autoAdvancing) return "Starting...";
    if (totalPlayers === 0) return "Let's Go";
    if (readyPlayers === 0) return "Waiting for Players...";
    if (allPlayersReady) return "Let's Go";
    return `${readyPlayers}/${totalPlayers} Players Ready`;
  };

  return (
    <Root>
      <Background />
      <InstructionsCard>
        <Title>Game Instructions</Title>
        
        <Container maxWidth="md" disableGutters>
          <Grid container spacing={1}>
            {instructionsData.map((item, index) => (
              <Grid item xs={12} key={index}>
                <InstructionItem style={{ animationDelay: `${index * 0.05}s` }}>
                  <Emoji>{item.emoji}</Emoji>
                  <InstructionText>{item.text}</InstructionText>
                </InstructionItem>
              </Grid>
            ))}
          </Grid>
        </Container>
      </InstructionsCard>
      
      {/* Player Readiness Panel */}
      {totalPlayers > 0 && (
        <PlayerReadinessPanel>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Player Status
            </Typography>
            <Chip 
              size="small" 
              label={`${readyPlayers}/${totalPlayers} Ready`}
              sx={{ 
                backgroundColor: allPlayersReady ? ACCENT_TEAL : ACCENT_YELLOW,
                color: '#222',
                fontWeight: 'bold'
              }}
            />
          </Box>
          <List dense disablePadding>
            {Object.entries(playersReadiness).map(([playerId, playerData]) => (
              <PlayerStatusListItem key={playerId} isready={playerData.isReady.toString()}>
                {playerData.isReady ? (
                  <CheckCircleIcon sx={{ mr: 1, color: ACCENT_TEAL }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ mr: 1, color: '#aaa' }} />
                )}
                <ListItemText 
                  primary={playerData.playerName} 
                  sx={{ margin: 0 }}
                />
              </PlayerStatusListItem>
            ))}
          </List>
        </PlayerReadinessPanel>
      )}
      
      <SkipButton 
        onClick={handleAdvanceToPractice}
        endIcon={<span style={{ fontSize: '1.2rem' }}>→</span>}
        disabled={autoAdvancing}
      >
        {getButtonText()}
      </SkipButton>
    </Root>
  );
};

export default InstructionsScreen; 